import { 
  PlanogramPlan, 
  PlanFilters, 
  PlanPaginationParams, 
  ApiResponse, 
  PlanPaginatedResponse,
  PlanErrorInfo,
} from '../types';
import { Milestone } from '../types/plan';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { MockPlanService } from '../mock';
import PlanogramSubtaskService from './planogramSubtaskService';
import { WHOLE_USE_MOCK } from './api';
import httpClient from './httpClient';

/**
 * Plan Service Class
 * Handles all plan-related API operations with support for both mock and real API calls
 */
export class PlanService {
  private useMockData: boolean;
  
  // 静态实例和配置
  private static instance: PlanService;
  private static useMock: boolean = true;

  constructor(useMockData: boolean = true) {
    this.useMockData = useMockData;
  }
  
  /**
   * 获取单例实例
   */
  public static getInstance(): PlanService {
    if (!PlanService.instance) {
      PlanService.instance = new PlanService(PlanService.useMock);
    }
    return PlanService.instance;
  }
  
  /**
   * 设置是否使用 mock 数据
   * @param useMock 是否使用 mock 数据
   */
  public static setUseMock(useMock: boolean): void {
    PlanService.useMock = useMock;
    if (PlanService.instance) {
      PlanService.instance.useMockData = useMock;
    }
  }
  
  /**
   * 获取当前是否使用 mock 数据
   */
  public static getUseMock(): boolean {
    return PlanService.useMock;
  }


  /**
   * Handle API errors with proper error formatting
   */
  private handleError(error: any, operation: string): PlanErrorInfo {
    console.error(`Plan Service - ${operation} error:`, error);
    
    return {
      type: 'network',
      message: error.message || `${operation}操作失败`,
      code: error.code || 'UNKNOWN_ERROR',
      traceId: error.traceId,
    };
  }

  /**
   * Get plans list with filtering, sorting, and pagination
   */
  async getPlans(
    filters: PlanFilters = {},
    pagination: PlanPaginationParams
  ): Promise<ApiResponse<PlanPaginatedResponse<PlanogramPlan>>> {
    try {

      if (this.useMockData && WHOLE_USE_MOCK) {
        return this.getMockPlans(filters, pagination);
      }

      // Real API call implementation
      const queryParams = {
        ...filters as any,
        currentPage: pagination.currentPage.toString(),
        pageSize: pagination.pageSize.toString(),
        sortBy: pagination.sortBy || 'gmtModified',
        sortOrder: pagination.sortOrder || 'desc',
      }

      const response = await httpClient.post(API_ENDPOINTS.PLAN.PAGE, queryParams);
      return response.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '获取计划列表').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  /**
   * Get plan details by ID
   */
  async getPlanById(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {

      if (this.useMockData && WHOLE_USE_MOCK) {
        return this.getMockPlanDetail(id);
      }

      // Real API call implementation
      const response = await httpClient.post(API_ENDPOINTS.PLAN.QUERY_BY_ID, {planId:id});
      return response.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '获取计划详情').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  /**
   * Get milestones for a plan by ID
   */
  async getMilestonesByPlan(id: string): Promise<ApiResponse<Milestone[]>> {
    try {
      if (this.useMockData && WHOLE_USE_MOCK) {
        return this.getMockMilestonesByPlan(id);
      }

      // Real API call implementation would go here
      // For now, we'll fall back to mock implementation
      let res = await httpClient.post(API_ENDPOINTS.PLAN.QUERY_PLAN_DETAILS, {
        planId: id
      });
      return res.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '获取计划里程碑').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  /**
   * Activate a plan
   */
  async activatePlan(id: string, formData?: any): Promise<ApiResponse<PlanogramPlan>> {
    try {

      if (this.useMockData && WHOLE_USE_MOCK) {
        const result = await this.activateMockPlan(id);
        
        // 如果计划激活成功，恢复相关暂停子任务状态
        if (result.success && result.data) {
          try {
            await PlanogramSubtaskService.batchUpdateSubtasksByPlan(
              result.data.planCode,
              'Active',
              '计划重新激活，相关暂停子任务恢复为待开始状态'
            );
          } catch (subtaskError) {
            console.warn('批量恢复子任务状态失败:', subtaskError);
            // 不影响计划激活的主要操作
          }
        }
        
        return result;
      }

      // Real API call implementation
      const requestData: any = {
        planId: id
      };
      
      // 如果有表单数据，则添加到请求中
      if (formData) {
        if (formData.plannedStartTime) {
          // 将plannedStartTime转换为时间戳格式
          requestData.plannedStartTime = formData.plannedStartTime.valueOf();
        }
        if (formData.description !== undefined) {
          requestData.description = formData.description;
        }
      }

      const response = await httpClient.post(API_ENDPOINTS.PLAN.PLAN_ACTIVATE, requestData);
      
      // 如果计划激活成功，恢复相关暂停子任务状态
      if (response.data.success && response.data.data) {
        try {
          await PlanogramSubtaskService.batchUpdateSubtasksByPlan(
            response.data.data.planCode,
            'Active',
            '计划重新激活，相关暂停子任务恢复为待开始状态'
          );
        } catch (subtaskError) {
          console.warn('批量恢复子任务状态失败:', subtaskError);
          // 不影响计划激活的主要操作
        }
      }
      
      return response.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '启动计划').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  /**
   * Pause a plan
   */
  async pausePlan(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {

      if (this.useMockData && WHOLE_USE_MOCK) {
        const result = await this.pauseMockPlan(id);
        
        // 如果计划暂停成功，批量更新相关子任务状态为暂停
        if (result.success && result.data) {
          try {
            await PlanogramSubtaskService.batchUpdateSubtasksByPlan(
              result.data.planCode,
              'Paused',
              '计划暂停，相关子任务自动暂停'
            );
          } catch (subtaskError) {
            console.warn('批量更新子任务状态失败:', subtaskError);
            // 不影响计划暂停的主要操作
          }
        }
        
        return result;
      }

      // Real API call implementation
      const response = await httpClient.post(API_ENDPOINTS.PLAN.PLAN_PAUSE, {
        planId: id
      });
      
      // 如果计划暂停成功，批量更新相关子任务状态为暂停
      if (response.data.success && response.data.data) {
        try {
          await PlanogramSubtaskService.batchUpdateSubtasksByPlan(
            response.data.data.planCode,
            'Paused',
            '计划暂停，相关子任务自动暂停'
          );
        } catch (subtaskError) {
          console.warn('批量更新子任务状态失败:', subtaskError);
          // 不影响计划暂停的主要操作
        }
      }
      
      return response.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '暂停计划').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  /**
   * Cancel a plan
   */
  async cancelPlan(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {

      if (this.useMockData && WHOLE_USE_MOCK) {
        return this.cancelMockPlan(id);
      }

      // Real API call implementation
      const response = await httpClient.post(API_ENDPOINTS.PLAN.PLAN_CANCEL, {
        planId: id
      });
      return response.data;
    } catch (error) {
      return {
        traceId: '',
        success: false,
        errorMessage: this.handleError(error, '取消计划').message,
        flag: 'fail',
        data: null,
      };
    }
  }

  // Mock data methods (using the unified mock service)
  private async getMockPlans(filters: PlanFilters, pagination: PlanPaginationParams): Promise<ApiResponse<PlanPaginatedResponse<PlanogramPlan>>> {
    try {
      const mockFilters = {
        status: filters.status ? [filters.status] : undefined,
        planType: filters.planType ? [filters.planType] : undefined,
        templateOwner: filters.templateOwner,
        searchKeyword: filters.keyword,
        page: pagination.currentPage,
        pageSize: pagination.pageSize
      };
      
      const result = await MockPlanService.getPlanList(mockFilters);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: result.success,
        errorMessage: result.success ? null : result.message,
        flag: result.success ? 'success' : 'fail',
        data: result.success ? {
          tableData: result.data.plans,
          paging: {
            currentPage: result.data.page,
            totalCount: result.data.total,
            pageSize: result.data.pageSize
          }
        } : null
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail',
        data: null
      };
    }
  }

  private async getMockPlanDetail(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {
      const result = await MockPlanService.getPlanDetails(id);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: result.success,
        errorMessage: result.success ? null : result.message,
        flag: result.success ? 'success' : 'fail',
        data: result.success ? result.data : null
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail',
        data: null
      };
    }
  }

  private async getMockMilestonesByPlan(id: string): Promise<ApiResponse<Milestone[]>> {
    try {
      // Import mock data
      const { mockMilestones } = await import('../mock');
      
      // Get milestones for this plan
      const planMilestones = mockMilestones.filter(m => m.id === id);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: true,
        errorMessage: null,
        flag: 'success' as const,
        data: planMilestones
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail' as const,
        data: null
      };
    }
  }

  private async activateMockPlan(id: string, formData?: any): Promise<ApiResponse<PlanogramPlan>> {
    try {
      const result = await MockPlanService.activatePlan(id, formData);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: result.success,
        errorMessage: result.success ? null : result.message,
        flag: result.success ? 'success' : 'fail',
        data: result.success ? result.data : null
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail',
        data: null
      };
    }
  }

  private async pauseMockPlan(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {
      const result = await MockPlanService.pausePlan(id);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: result.success,
        errorMessage: result.success ? null : result.message,
        flag: result.success ? 'success' : 'fail',
        data: result.success ? result.data : null
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail',
        data: null
      };
    }
  }

  private async cancelMockPlan(id: string): Promise<ApiResponse<PlanogramPlan>> {
    try {
      const result = await MockPlanService.cancelPlan(id);
      
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: result.success,
        errorMessage: result.success ? null : result.message,
        flag: result.success ? 'success' : 'fail',
        data: result.success ? result.data : null
      };
    } catch (error: any) {
      return {
        traceId: 'mock-trace-' + Date.now(),
        success: false,
        errorMessage: error.message,
        flag: 'fail',
        data: null
      };
    }
  }
}

// Create and export a default instance
const planService = PlanService.getInstance(); // 使用单例模式
export default planService;